import { useCallback, useEffect, useState } from 'react';
import { Button } from './Button';
import { Container } from './Container';
import { Icon } from './Icon';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { navLinks } from '@/content/site';
import { useScrolled } from '@/hooks/useScrolled';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import { useTheme } from '@/hooks/useTheme';
import { cx } from '@/lib/cx';
import styles from './Header.module.css';

const SECTION_IDS = navLinks.map((link) => link.id);
const MOBILE_PANEL_ID = 'primary-navigation';

export function Header() {
  const { theme, toggleTheme } = useTheme();
  const scrolled = useScrolled();
  const activeId = useScrollSpy(SECTION_IDS);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Escape closes the panel; widening past the breakpoint makes it irrelevant.
  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    const desktop = window.matchMedia('(min-width: 861px)');
    const onBreakpoint = (event: MediaQueryListEvent) => {
      if (event.matches) closeMenu();
    };

    document.addEventListener('keydown', onKeyDown);
    desktop.addEventListener('change', onBreakpoint);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      desktop.removeEventListener('change', onBreakpoint);
    };
  }, [menuOpen, closeMenu]);

  return (
    <header className={cx(styles.header, scrolled && styles.scrolled)}>
      <Container className={styles.inner}>
        <Logo />

        <nav className={styles.nav} aria-label="Primary">
          <ul className={styles.list}>
            {navLinks.map((link) => (
              <li key={link.id}>
                <a
                  href={`#${link.id}`}
                  className={styles.link}
                  aria-current={activeId === link.id ? 'true' : undefined}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.actions}>
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          <Button href="#contact" className={styles.cta} icon="arrowRight">
            Start a project
          </Button>
          <button
            type="button"
            className={styles.burger}
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls={MOBILE_PANEL_ID}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} />
          </button>
        </div>
      </Container>

      <div id={MOBILE_PANEL_ID} className={styles.panel} hidden={!menuOpen}>
        <Container>
          <ul className={styles.panelList}>
            {navLinks.map((link) => (
              <li key={link.id}>
                <a
                  href={`#${link.id}`}
                  className={styles.panelLink}
                  aria-current={activeId === link.id ? 'true' : undefined}
                  onClick={closeMenu}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <Button href="#contact" size="lg" block icon="arrowRight" onClick={closeMenu}>
            Start a project
          </Button>
        </Container>
      </div>
    </header>
  );
}
